import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getBrands } from '@/api-clients/get-brands';
import { getLocations } from '@/api-clients/get-locations';
import { getPositions } from '@/api-clients/get-positions';
import { useTableContext } from '@/contexts/table-context';
import { useFilterQuery } from '@/hooks/use-filter-query';

export function useOpeningsFilters() {
  const intl = useIntl();

  const {
    filters: {
      positionIds,
      locationIds,
      isSelectedForSourcing,
      brandIds,
      active,
    },
  } = useTableContext();

  const {
    data: positionsData,
    isFetching: isFetchingPositions,
    query: positionsQuery,
    setQuery: setPositionsQuery,
  } = useFilterQuery({
    key: 'positions-filter',
    fetchFn: getPositions,
  });

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    query: locationsQuery,
    setQuery: setLocationsQuery,
  } = useFilterQuery({
    key: 'locations-filter',
    fetchFn: getLocations,
  });

  const {
    data: brandsData,
    isFetching: isFetchingBrands,
    query: brandsQuery,
    setQuery: setBrandsQuery,
  } = useFilterQuery({
    key: 'brands-filter',
    fetchFn: getBrands,
  });

  const brandsOptions = brandsData?.brands?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const filters = useMemo(
    () => [
      {
        filterKey: 'isSelectedForSourcing',
        label: intl.formatMessage({ defaultMessage: 'Dashboard status' }),
        options: [
          // This is defined as a string to be consistent with the combobox component. It will be converted to a boolean when sent to the backend.
          {
            label: intl.formatMessage({ defaultMessage: 'All' }),
            value: undefined,
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Added' }),
            value: true,
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Not added',
            }),
            value: false,
          },
        ],
        isMultiSelect: false,
        selectedValues: [isSelectedForSourcing],
      },
      {
        filterKey: 'active',
        label: intl.formatMessage({ defaultMessage: 'Status' }),
        options: [
          {
            label: intl.formatMessage({ defaultMessage: 'All' }),
            value: undefined,
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Active' }),
            value: true,
          },
          {
            label: intl.formatMessage({ defaultMessage: 'Inactive' }),
            value: false,
          },
        ],
        isMultiSelect: false,
        selectedValues: [active],
      },
      {
        filterKey: 'brandIds',
        label: intl.formatMessage({ defaultMessage: 'Brand' }),
        isFetching: isFetchingBrands,
        query: brandsQuery,
        setQuery: setBrandsQuery,
        options: brandsOptions,
        isMultiSelect: true,
        selectedValues: brandIds,
      },
      {
        filterKey: 'positionIds',
        label: intl.formatMessage({ defaultMessage: 'Position' }),
        isFetching: isFetchingPositions,
        query: positionsQuery,
        setQuery: setPositionsQuery,
        options: positionsData?.positions?.map(({ id, title }) => ({
          label: title,
          value: id,
        })),
        isMultiSelect: true,
        selectedValues: positionIds,
      },
      {
        filterKey: 'locationIds',
        label: intl.formatMessage({ defaultMessage: 'Location' }),
        isFetching: isFetchingLocations,
        query: locationsQuery,
        setQuery: setLocationsQuery,
        options: locationsData?.locations?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        isMultiSelect: true,
        selectedValues: locationIds,
      },
    ],
    [
      intl,
      isSelectedForSourcing,
      isFetchingPositions,
      positionsQuery,
      setPositionsQuery,
      positionsData?.positions,
      positionIds,
      isFetchingLocations,
      locationsQuery,
      setLocationsQuery,
      locationsData?.locations,
      locationIds,
      brandIds,
      brandsOptions,
      active,
      isFetchingBrands,
      brandsQuery,
      setBrandsQuery,
    ],
  );

  return filters;
}
